import apiRequest, { authStorage } from './apiClient'

const toFormData = (payload = {}) => {
  if (payload instanceof FormData) return payload

  const formData = new FormData()

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return

    if (Array.isArray(value)) {
      value.forEach((item) => formData.append(`${key}[]`, item))
      return
    }

    formData.append(key, value)
  })

  return formData
}

const multipartRequest = (endpoint, payload, method = 'POST') =>
  apiRequest(endpoint, {
    method,
    body: toFormData(payload),
    isFormData: true,
  })

export const merchantApi = {
  auth: {
    requestOtp: (payload) =>
      apiRequest('/auth/request-otp', {
        method: 'POST',
        body: payload,
        auth: false,
      }),

    login: async (payload) => {
      const response = await apiRequest('/auth/login', {
        method: 'POST',
        body: payload,
        auth: false,
      })

      const token =
        response?.data?.token ||
        response?.data?.access_token ||
        response?.token ||
        response?.access_token

      if (token) authStorage.setToken(token)

      return response
    },

    me: () => apiRequest('/auth/me'),

    logout: async () => {
      try {
        return await apiRequest('/auth/logout', { method: 'POST' })
      } finally {
        authStorage.clearToken()
      }
    },
  },

  general: {
    get: () => apiRequest('/general'),
  },

  profile: {
    get: () => apiRequest('/auth/profile'),

    update: (payload) =>
      apiRequest('/auth/profile', {
        method: 'PUT',
        body: payload,
      }),

    updateMultipart: (payload) =>
      multipartRequest('/auth/profile', {
        _method: 'PUT',
        ...payload,
      }),

    removePhoto: () =>
      apiRequest('/auth/profile/photo', {
        method: 'DELETE',
      }),
  },

  adminUsers: {
    list: (params) => apiRequest('/admin-users', { params }),

    invite: (payload) =>
      apiRequest('/admin-users/invite', {
        method: 'POST',
        body: payload,
      }),

    show: (adminUserId) =>
      apiRequest(`/admin-users/${adminUserId}`),

    update: (adminUserId, payload) =>
      apiRequest(`/admin-users/${adminUserId}`, {
        method: 'PUT',
        body: payload,
      }),

    activate: (adminUserId) =>
      apiRequest(`/admin-users/${adminUserId}/active`, {
        method: 'PATCH',
      }),

    deactivate: (adminUserId) =>
      apiRequest(`/admin-users/${adminUserId}/deactivated`, {
        method: 'PATCH',
      }),

    remove: (adminUserId) =>
      apiRequest(`/admin-users/${adminUserId}`, {
        method: 'DELETE',
      }),
  },

  permissions: {
    list: (params) => apiRequest('/permissions', { params }),
  },

  roles: {
    list: (params) => apiRequest('/roles', { params }),

    create: (payload) =>
      apiRequest('/roles/create', {
        method: 'POST',
        body: payload,
      }),

    show: (roleId) => apiRequest(`/roles/${roleId}`),

    update: (roleId, payload) =>
      apiRequest(`/roles/${roleId}`, {
        method: 'PUT',
        body: payload,
      }),

    remove: (roleId) =>
      apiRequest(`/roles/${roleId}`, {
        method: 'DELETE',
      }),

    // Marked as "REMOVED" in the Postman collection. Kept here so the
    // service mirrors every collection request.
    syncPermissions: (roleId, payload) =>
      apiRequest(`/roles/${roleId}/permissions`, {
        method: 'PUT',
        body: payload,
      }),
  },

  settings: {
    get: () => apiRequest('/settings'),

    update: (payload) =>
      apiRequest('/settings', {
        method: 'PUT',
        body: payload,
      }),
  },

  merchants: {
    list: (params) => apiRequest('/merchants', { params }),

    export: (params) => apiRequest('/merchants/export', {
      params,
      responseType: 'blob',
    }),

    show: (merchantId) =>
      apiRequest(`/merchants/${merchantId}`),

    outlets: (merchantId, params) =>
      apiRequest(`/merchants/${merchantId}/outlets`, { params }),

    catalog: (merchantId, params) =>
      apiRequest(`/merchants/${merchantId}/catalog`, { params }),

    orders: (merchantId, params) =>
      apiRequest(`/merchants/${merchantId}/orders`, { params }),

    branding: {
      get: (merchantId) =>
        apiRequest(`/merchants/${merchantId}/branding`),

      update: (merchantId, payload) =>
        multipartRequest(`/merchants/${merchantId}/branding`, payload),
    },

    updateStatus: (merchantId, payload) =>
      apiRequest(`/merchants/${merchantId}/status`, {
        method: 'PATCH',
        body: payload,
      }),

    sync: {
      all: (merchantId) =>
        apiRequest(`/merchants/${merchantId}/sync`, {
          method: 'POST',
        }),

      registerSource: (payload) =>
        apiRequest('/merchants/register-sync-source', {
          method: 'POST',
          body: payload,
        }),

      overview: (merchantId) =>
        apiRequest(`/merchants/${merchantId}/sync/overview`, {
          method: 'POST',
        }),

      outlets: (merchantId) =>
        apiRequest(`/merchants/${merchantId}/sync/outlets`, {
          method: 'POST',
        }),

      orders: (merchantId, params) =>
        apiRequest(`/merchants/${merchantId}/sync/orders`, {
          method: 'POST',
          params,
        }),

      branding: (merchantId) =>
        apiRequest(`/merchants/${merchantId}/sync/branding`, {
          method: 'POST',
        }),

      catalog: (merchantId) =>
        apiRequest(`/merchants/${merchantId}/sync/catalog`, {
          method: 'POST',
        }),
    },
  },

  dashboard: {
    get: (params) => apiRequest('/dashboard', { params }),
  },

  reports: {
    gmvOrders: (params) =>
      apiRequest('/reports/gmv-orders', { params }),

    merchantSales: (params) =>
      apiRequest('/reports/merchant-sales', { params }),
  },

  posIntegrations: {
    summary: (params) =>
      apiRequest('/pos-integrations/summary', { params }),

    logs: (params) =>
      apiRequest('/pos-integrations/logs', { params }),
    logsDetails: (id) =>
      apiRequest(`/pos-integrations/logs/${id}`),
  },
}

export default merchantApi
